import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { searchProps, useSearch } from '../../hooks/useSearch'
import { paginationProps, usePagination } from '../../hooks/usePagination'
import { AppStore } from '../../store/applicationState'
import { Table } from '../../components/ui/adminComponents/Table'
import { Input } from '../../components/ui/adminComponents/Input'
import { LoaderPoints } from '../../components/ui/LoaderPoints'
import {
  getFavoritesActivityChildParsed,
  getFavoritesActivityParsed,
} from '../../utils/statisticsUtils/statisticDataHelpers'
import { GetFavoritesActivity, GetFavoritesActivityChild } from '../../store/admin/statistics/favoritesActivity/actions'

interface Props {
  currentTenderId: number
}

const limitAmount = 400
const initialParams = { limit: limitAmount, offset: 0, search: undefined, sort: '', sortBy: '' }

const favoritesActivityThead = {
  stock_number: 'Stock #',
  title: 'Title',
  count_of_wishlists: 'Uniq favorites by customers',
  action: 'Action',
}

const favoritesActivityTheadChild = {
  email: 'Customer email',
  company: 'Company',
  timestamp: 'Timestamp',
}

const tableCellWidth = [120, 370, 220, 140]

export const FavoritesActivity: React.FC<Props> = ({ currentTenderId }) => {
  const [params, setParams] = useState<searchProps | paginationProps>(initialParams)
  const [dispatchTime, setDispatchTime] = useState(0)
  const [childIndex, setChildIndex] = useState<number[]>([])
  const dispatch = useDispatch()
  const { tenders, favoritesActivity } = useSelector((store: AppStore) => store)

  const didMount = useRef(false)

  usePagination(favoritesActivity.data, setParams, limitAmount)
  useSearch(limitAmount, params, setParams, setDispatchTime)

  useEffect(() => {
    if (didMount.current) {
      setDispatchTime((prev) => prev + 1)
      setParams(initialParams)
    }
    didMount.current = true
  }, [currentTenderId])

  useEffect(() => {
    dispatch(
      GetFavoritesActivity.request({
        idTender: currentTenderId,
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        sortBy: params.sortBy,
        sort: params.sort,
      })
    )
  }, [params.offset, params.limit, params.sortBy, params.sort, dispatchTime])

  useEffect(() => {
    if (tenders.updateTenderNow) {
      if (params.offset === 0) setDispatchTime((prev) => prev + 1)
      else setParams((prev) => ({ ...prev, offset: 0, limit: prev.limit + prev.offset }))
    }
  }, [tenders.updateTenderNow])

  /// /////////////////////////////////////////////////////////////////////////////////////////////
  const callBack = (sortBy: string) => {
    if (sortBy === 'action') return

    let value: { sortBy: string; sort: 'ASC' | 'DESC' }
    if (sortBy !== params.sortBy || params.sort === 'DESC') value = { sortBy, sort: 'ASC' }
    else value = { sortBy, sort: 'DESC' }

    setParams((prev) => ({ ...prev, offset: 0, limit: limitAmount, sortBy: value.sortBy, sort: value.sort }))
    setChildIndex([])
  }

  const getChildForFavoritesActivity = (idItem: number) => {
    if (idItem !== -1) {
      dispatch(GetFavoritesActivityChild.request({ idItem, idTender: currentTenderId }))
    }
  }

  const favoritesActivityValues = useMemo(() => {
    return favoritesActivity.data
      ? getFavoritesActivityParsed(favoritesActivity.data, childIndex, getChildForFavoritesActivity, currentTenderId)
      : null
  }, [favoritesActivity.data, childIndex])

  const favoritesActivityValuesChild = useMemo(() => {
    if (!favoritesActivity.childData) return null
    const result = new Map<number, object[]>()
    favoritesActivity.childData.forEach((value, key) => {
      result.set(key, getFavoritesActivityChildParsed(value))
    })
    return result
  }, [favoritesActivity.childData?.entries()])

  useEffect(() => {
    setChildIndex(
      childIndex.filter(
        (child) =>
          favoritesActivity.data &&
          favoritesActivity.data.some((item) => +`${currentTenderId}${item.item_id}` === child)
      )
    )
  }, [favoritesActivity.data])

  useEffect(() => {
    if (tenders.updateTenderNow) {
      if (childIndex.length) {
        childIndex.map((child) => {
          if (favoritesActivity.data)
            getChildForFavoritesActivity(child - currentTenderId * currentTenderId.toString().length)

          return null
        })
      }
    }
  }, [tenders.updateTenderNow])

  const favoritesActivityTable = useMemo(() => {
    return favoritesActivityValues ? (
      <Table
        thead={favoritesActivityThead}
        tbody={favoritesActivityValues}
        theadChild={favoritesActivityTheadChild}
        tbodyChild={favoritesActivityValuesChild}
        callBack={callBack}
        childIndex={childIndex}
        setChildIndex={setChildIndex}
        tableCellWidth={tableCellWidth}
      />
    ) : null
  }, [favoritesActivityValues, favoritesActivityValuesChild])

  return (
    <>
      <div className='flex justify-between lg:items-center mb-4 flex-col lg:flex-row'>
        <div className='w-full md:w-1/3 lg:w-1/4 mb-6 lg:mb-0'>
          <Input
            placeholder='Search...'
            value={params.search || ''}
            onChange={(e) => setParams((prev) => ({ ...prev, search: e.target.value }))}
          />
        </div>
      </div>
      <div className='relative'>
        {favoritesActivityTable}
        {!!favoritesActivityTable && favoritesActivity.loading && (
          <div className='absolute left-0 right-0 -bottom-6 flex justify-center'>
            <LoaderPoints className='m-auto' />
          </div>
        )}
      </div>
    </>
  )
}
